import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchApi } from '../services/api';

interface Customer {
  고객번호: number;
  업체번호: number;
  고객명: string;
  성별: string;
  영문성: string;
  영문이름: string;
  대표전화: string;
  이메일: string;
  문자수신동의: boolean;
  이메일수신동의: boolean;
  상태코드: string;
  가입경로: string;
  최근접속일시: string;
  등록일시: string;
  변경일시: string;
  비고: string;
}

interface CustomerDetailDialogProps {
  open: boolean;
  onClose: () => void;
  customer: Customer | null;
  onSave: () => void;
}

const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
  open,
  onClose,
  customer,
  onSave,
}) => {
  const [formData, setFormData] = useState<Customer>({
    고객번호: 0,
    업체번호: Number(localStorage.getItem('CompanyNo')),
    고객명: '',
    성별: '',
    영문성: '',
    영문이름: '',
    대표전화: '',
    이메일: '',
    문자수신동의: false,
    이메일수신동의: false,
    상태코드: '',
    가입경로: '',
    최근접속일시: '',
    등록일시: '',
    변경일시: '',
    비고: '',
  });

  useEffect(() => {
    if (customer) {
      setFormData(customer);
    } else {
      setFormData({
        고객번호: 0,
        업체번호: Number(localStorage.getItem('CompanyNo')),
        고객명: '',
        성별: '',
        영문성: '',
        영문이름: '',
        대표전화: '',
        이메일: '',
        문자수신동의: false,
        이메일수신동의: false,
        상태코드: '',
        가입경로: '',
        최근접속일시: '',
        등록일시: '',
        변경일시: '',
        비고: '',
      });
    }
  }, [customer]);

  const handleChange = (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleCheckboxChange = (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
      [`${field}일시`]: event.target.checked ? new Date().toISOString() : '',
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await fetchApi('ITN_T_간편일정고객저장', [
        { "컬럼명": "@업체번호", "값": formData.업체번호.toString() },
        { "컬럼명": "@고객번호", "값": formData.고객번호.toString() },
        { "컬럼명": "@이메일", "값": formData.이메일 },
        { "컬럼명": "@고객명", "값": formData.고객명 },
        { "컬럼명": "@성별", "값": formData.성별 },
        { "컬럼명": "@영문성", "값": formData.영문성 },
        { "컬럼명": "@영문이름", "값": formData.영문이름 },
        { "컬럼명": "@대표전화", "값": formData.대표전화 },
        { "컬럼명": "@문자수신동의", "값": formData.문자수신동의 ? "1" : "0" },
        { "컬럼명": "@이메일수신동의", "값": formData.이메일수신동의 ? "1" : "0" },
        { "컬럼명": "@상태코드", "값": formData.상태코드 },
        { "컬럼명": "@가입경로", "값": formData.가입경로 },
        { "컬럼명": "@비고", "값": formData.비고 },
      ]);

      if (response.data) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Failed to save customer:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {customer ? '고객 정보 수정' : '신규 고객 등록'}
          </Typography>
        </Box>
        <Box>

          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{ 
                height: '36px',
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}>
            저장
          </Button>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              ml: 1,
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent >
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="고객명"
              value={formData.고객명}
              onChange={handleChange('고객명')}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>성별</InputLabel>
              <Select
                value={formData.성별}
                onChange={(e) => setFormData({ ...formData, 성별: e.target.value })}>
                <MenuItem value="남성">남성</MenuItem>
                <MenuItem value="여성">여성</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="영문 성"
              value={formData.영문성}
              onChange={handleChange('영문성')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="영문 이름"
              value={formData.영문이름}
              onChange={handleChange('영문이름')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="이메일"
              value={formData.이메일}
              onChange={handleChange('이메일')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="연락처"
              value={formData.대표전화}
              onChange={handleChange('대표전화')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.문자수신동의}
                  onChange={handleCheckboxChange('문자수신동의')}
                />
              }
              label="문자 수신 동의"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.이메일수신동의}
                  onChange={handleCheckboxChange('이메일수신동의')}
                />
              }
              label="이메일 수신 동의"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="비고"
              value={formData.비고}
              onChange={handleChange('비고')}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDetailDialog; 